import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSelector from '../LanguageSelector';
import { calculateScore } from '../../utils/scoreCalculation';
import ReviewForm from '../ReviewForm';
import socket from '../../utils/socket';

/**
 * GameOverModal Component
 * Displays game over screen with leaderboard, scores, and action buttons
 *
 * @param {boolean} show - Whether to show the modal (game status === 'finished')
 * @param {string} message - Game over message
 * @param {object} allPlayerScores - Object mapping player names to score data
 * @param {object} allPlayerScorecards - Object mapping player names to scorecard data
 * @param {object} allPlayerClosedRows - Global closed rows object
 * @param {object} allPlayerRowClosers - Object with arrays of player names who closed each row
 * @param {object} gameWinner - Winner object with name property
 * @param {Array} players - Array of player objects
 * @param {string} playerName - Current player's name
 * @param {function} onPlayAgain - Callback for starting new game (host only)
 * @param {function} onBackToHome - Callback for returning to home
 * @param {function} onOpenRules - Callback for opening rules modal
 * @param {string} gameId - Game ID to display
 */
const GameOverModal = ({
  show,
  message,
  allPlayerScores,
  allPlayerScorecards,
  allPlayerClosedRows,
  allPlayerRowClosers,
  gameWinner,
  players,
  playerName,
  onPlayAgain,
  onBackToHome,
  onOpenRules,
  gameId
}) => {
  const { t } = useLanguage();

  // Review system state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewSkipped, setReviewSkipped] = useState(false);
  const [shouldPromptReview, setShouldPromptReview] = useState(false);

  // Track which players are currently reviewing
  const [playersReviewing, setPlayersReviewing] = useState(new Set());

  // Listen for review form state changes from other players
  useEffect(() => {
    if (!socket) return;

    const handlePlayerReviewingUpdate = ({ playerName, isReviewing }) => {
      setPlayersReviewing(prev => {
        const updated = new Set(prev);
        if (isReviewing) {
          updated.add(playerName);
        } else {
          updated.delete(playerName);
        }
        return updated;
      });
    };

    socket.on('playerReviewingUpdate', handlePlayerReviewingUpdate);

    return () => {
      socket.off('playerReviewingUpdate', handlePlayerReviewingUpdate);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers for review form open/close with socket emit
  const handleOpenReviewForm = () => {
    setShowReviewForm(true);
    if (socket && gameId) {
      socket.emit('reviewFormOpened', { gameId, playerName });
    }
  };

  const handleCloseReviewForm = () => {
    setShowReviewForm(false);
    if (socket && gameId) {
      socket.emit('reviewFormClosed', { gameId, playerName });
    }
  };

  const handleSkipReview = () => {
    setShowReviewForm(false);
    setReviewSkipped(true);
    if (socket && gameId) {
      socket.emit('reviewFormClosed', { gameId, playerName });
    }
  };

  const handleSubmitReview = () => {
    setReviewSubmitted(true);
    setShowReviewForm(false);
    if (socket && gameId) {
      socket.emit('reviewFormClosed', { gameId, playerName });
    }
  };

  // Check if user should be prompted for review (once per week)
  useEffect(() => {
    if (!show) return;

    // Check if review was submitted within the last week
    const lastReviewTime = localStorage.getItem('qwixx_review_submitted_time');
    if (lastReviewTime) {
      const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
      const timeSinceReview = Date.now() - parseInt(lastReviewTime, 10);
      if (timeSinceReview < oneWeekMs) {
        setShouldPromptReview(false);
        return;
      }
    }

    // Show review prompt (first time or more than a week since last review)
    setShouldPromptReview(true);

    // Increment games played counter (for statistics)
    const gamesPlayed = parseInt(localStorage.getItem('qwixx_games_played') || '0');
    localStorage.setItem('qwixx_games_played', (gamesPlayed + 1).toString());
  }, [show, playerName, gameWinner, allPlayerScores]);

  if (!show) return null;

  const isHost = players.find(p => p.name === playerName)?.isHost;

  return (
    <div className="game-over">
      {/* 3D Dice Header for Game Over screen */}
      <div className="dice-header" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '0.5rem', position: 'relative' }}>
        {/* Language selector - positioned like in Homepage */}
        <div style={{
          position: 'absolute',
          left: '-25px',
          top: '-20px',
          transform: 'scale(0.625)',
          transformOrigin: 'center left',
          zIndex: 10
        }}>
          <LanguageSelector />
        </div>

        {/* Information button - positioned opposite to language selector */}
        <div style={{
          position: 'absolute',
          right: '-15px',
          top: '-15px',
          zIndex: 10
        }}>
          <button
            className="info-button"
            onClick={onOpenRules}
            aria-label={t('gameRules.title')}
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.2s ease'
            }}
          >
            i
          </button>
        </div>

        <div className="dice-container">
          <div
            className="die red"
            onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateY(360deg)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateY(0deg)'}
          >
            <div className="die-face die-front one">
              <div className="die-dots">
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-back six">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-top two">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-bottom five">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-left four">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-right three">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          </div>

          <div
            className="die yellow"
            onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateX(360deg)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateX(0deg)'}
          >
            <div className="die-face die-front two">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-back five">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-top six">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-bottom one">
              <div className="die-dots">
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-left three">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-right four">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          </div>

          <div
            className="die green"
            onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateZ(360deg)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateZ(0deg)'}
          >
            <div className="die-face die-front three">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-back four">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-top one">
              <div className="die-dots">
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-bottom six">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-left five">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-right two">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          </div>

          <div
            className="die blue"
            onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate3d(1, 1, 1, 360deg)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate3d(1, 1, 1, 0deg)'}
          >
            <div className="die-face die-front four">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-back one">
              <div className="die-dots">
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-top three">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-bottom two">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-left six">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <div className="die-face die-right five">
              <div className="die-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Qwixx Online title and Game Over on separate lines */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <h1 style={{
          textAlign: 'center',
          marginBottom: '0.5rem',
          background: 'linear-gradient(to right, #ef4444, #f59e0b, #10b981, #3b82f6)',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)',
          fontSize: '2.5rem',
          fontWeight: '700',
          position: 'relative',
          display: 'inline-block',
          padding: '0 1rem',
          letterSpacing: '0.05em'
        }}>{t('gameTitle')}</h1>

        {/* Decorative separator line */}
        <div style={{
          height: '3px',
          width: '50%',
          margin: '0.5rem auto',
          background: 'linear-gradient(to right, #ef4444, #f59e0b, #10b981, #3b82f6)',
          borderRadius: '2px'
        }}></div>

        <h2 style={{ fontSize: '2em' }}>{t('gameOver.title')}</h2>
      </div>

      {/* Game over message */}
      <div className="game-over-message">
        {message || t('gameOver.defaultMessage')}
      </div>

      {/* Leaderboard */}
      <div className="leaderboard">
        <h2>{t('gameOver.leaderboard')}</h2>
        <table className="score-breakdown-table">
          <thead>
            <tr>
              <th>{t('gameOver.player')}</th>
              <th className="red-column">{t('colors.red')}</th>
              <th className="yellow-column">{t('colors.yellow')}</th>
              <th className="green-column">{t('colors.green')}</th>
              <th className="blue-column">{t('colors.blue')}</th>
              <th className="penalty-column">{t('penalties.label')}</th>
              <th>{t('gameOver.total')}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(allPlayerScores)
              .sort(([, scoreDataA], [, scoreDataB]) => {
                // Handle both old format (number) and new format (object with score property)
                const scoreA = typeof scoreDataA === 'number' ? scoreDataA : scoreDataA.score;
                const scoreB = typeof scoreDataB === 'number' ? scoreDataB : scoreDataB.score;
                return scoreB - scoreA;
              })
              .map(([playerName, scoreData]) => {
                // Get the player's scorecard from the detailed data, or use a default empty scorecard
                const playerScorecard = allPlayerScorecards[playerName] || {
                  red: [],
                  yellow: [],
                  green: [],
                  blue: [],
                  penalties: 0
                };

                // Use global closed rows (same for all players)
                // Closed rows are global to the game, not per-player
                // If allPlayerClosedRows is empty/undefined, infer from scorecard
                const playerClosedRows = (allPlayerClosedRows && Object.keys(allPlayerClosedRows).length > 0)
                  ? allPlayerClosedRows
                  : {
                      red: playerScorecard.red.includes(12) && playerScorecard.red.length >= 5,
                      yellow: playerScorecard.yellow.includes(12) && playerScorecard.yellow.length >= 5,
                      green: playerScorecard.green.includes(2) && playerScorecard.green.length >= 5,
                      blue: playerScorecard.blue.includes(2) && playerScorecard.blue.length >= 5
                    };

                // Use server-provided breakdown if available, otherwise calculate locally
                let breakdown;
                if (typeof scoreData === 'object' && scoreData.breakdown) {
                  // Server sent breakdown - use it directly (authoritative)
                  breakdown = scoreData.breakdown;
                } else {
                  // Fallback to local calculation for backwards compatibility
                  // Pass allPlayerRowClosers and playerName for proper lock bonus calculation
                  const calculated = calculateScore(
                    playerScorecard,
                    playerClosedRows,
                    allPlayerRowClosers || {},
                    playerName
                  );
                  breakdown = calculated.breakdown;
                }

                return (
                  <tr key={playerName} className={playerName === gameWinner?.name ? 'winner' : ''}>
                    <td>
                      {playerName} {playerName === gameWinner?.name && '👑'}
                    </td>
                    <td
                      className="red-column"
                      title={t('gameOver.scoreDetails', {
                        count: playerScorecard.red.length + (playerClosedRows.red ? 1 : 0),
                        color: t('colors.red').toLowerCase()
                      })}
                      style={{ cursor: 'help' }}
                    >
                      {breakdown.red}
                    </td>
                    <td
                      className="yellow-column"
                      title={t('gameOver.scoreDetails', {
                        count: playerScorecard.yellow.length + (playerClosedRows.yellow ? 1 : 0),
                        color: t('colors.yellow').toLowerCase()
                      })}
                      style={{ cursor: 'help' }}
                    >
                      {breakdown.yellow}
                    </td>
                    <td
                      className="green-column"
                      title={t('gameOver.scoreDetails', {
                        count: playerScorecard.green.length + (playerClosedRows.green ? 1 : 0),
                        color: t('colors.green').toLowerCase()
                      })}
                      style={{ cursor: 'help' }}
                    >
                      {breakdown.green}
                    </td>
                    <td
                      className="blue-column"
                      title={t('gameOver.scoreDetails', {
                        count: playerScorecard.blue.length + (playerClosedRows.blue ? 1 : 0),
                        color: t('colors.blue').toLowerCase()
                      })}
                      style={{ cursor: 'help' }}
                    >
                      {breakdown.blue}
                    </td>
                    <td
                      className="penalty-column"
                      title={t('gameOver.penaltyDetails', {
                        count: playerScorecard.penalties
                      })}
                      style={{ cursor: 'help' }}
                    >
                      {breakdown.penalties}
                    </td>
                    <td
                      className="total-score"
                      title={t('gameOver.totalDetails')}
                      style={{ cursor: 'help' }}
                    >
                      {breakdown.total}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Action buttons - prominently display restart for host */}
      <div className="game-over-actions" style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {isHost && (
          <div style={{ textAlign: 'center', margin: '1rem 0' }}>
            <button
              onClick={onPlayAgain}
              disabled={playersReviewing.size > 0}
              style={{
                backgroundColor: playersReviewing.size > 0 ? '#9ca3af' : '#10b981',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '1.1rem',
                border: 'none',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                cursor: playersReviewing.size > 0 ? 'not-allowed' : 'pointer',
                opacity: playersReviewing.size > 0 ? 0.6 : 1,
                width: '100%',
                maxWidth: '300px',
                margin: '0 auto'
              }}
            >
              {t('gameOver.playAgain')}
            </button>
            {playersReviewing.size > 0 && (
              <p style={{
                color: '#f59e0b',
                fontSize: '0.875rem',
                marginTop: '0.75rem',
                fontWeight: '500'
              }}>
                {Array.from(playersReviewing).join(', ')} {t('gameOver.isReviewing')}
              </p>
            )}
          </div>
        )}
        {!isHost && (
          <div style={{
            textAlign: 'center',
            margin: '1rem 0',
            color: '#ef4444',
            fontSize: '2em',
            fontWeight: 'bold'
          }}>
            {t('errors.hostOnly')}
          </div>
        )}

        {/* Review Prompt - Only show if appropriate */}
        {shouldPromptReview && !reviewSubmitted && !reviewSkipped && !showReviewForm && (
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            margin: '1.5rem 0',
            textAlign: 'center',
            color: 'white'
          }}>
            <p style={{ fontSize: '1.1rem', marginBottom: '1rem', fontWeight: '500' }}>
              ⭐ {t('gameOver.enjoyedGame')}
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={handleOpenReviewForm}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#667eea',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '1rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0px)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {t('gameOver.leaveReview')}
              </button>
              <button
                onClick={handleSkipReview}
                style={{
                  backgroundColor: 'transparent',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: '2px solid white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                {t('gameOver.skipReview')}
              </button>
            </div>
          </div>
        )}

        {/* Review Form - Slides in when activated */}
        {showReviewForm && !reviewSubmitted && (
          <ReviewForm
            playerName={playerName}
            gameId={gameId}
            onSubmit={handleSubmitReview}
            onCancel={handleCloseReviewForm}
          />
        )}

        {/* Success message after review submitted */}
        {reviewSubmitted && showReviewForm === false && (
          <div style={{
            background: '#10b981',
            borderRadius: '0.75rem',
            padding: '1rem',
            margin: '1rem 0',
            textAlign: 'center',
            color: 'white',
            fontWeight: '500'
          }}>
            ✅ {t('reviews.thankYou')}
          </div>
        )}

        <button
          onClick={onBackToHome}
          style={{
            backgroundColor: '#ef4444',
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.5rem',
            fontWeight: '500',
            border: 'none',
            cursor: 'pointer',
            width: '100%',
            maxWidth: '300px',
            margin: '0 auto'
          }}
        >
          {t('gameOver.backToHome')}
        </button>
      </div>

      {/* Game ID - Added for game over screen */}
      <div style={{
        textAlign: 'center',
        margin: '1.5rem 0 0 0',
        fontSize: '0.875rem',
        color: '#6b7280',
        display: 'block'
      }}>
        {t('common.gamePin', { gameId })}
      </div>

      {/* Copyright notice - Added for game over screen */}
      <div style={{
        textAlign: 'center',
        margin: '0.5rem 0 0 0',
        fontSize: '0.875rem',
        color: '#6b7280',
        paddingTop: '4px'
      }}>
        {t('common.copyright')}
      </div>

      {/* Disclaimer - Added for game over screen */}
      <div className="disclaimer-container">
        <span className="disclaimer-trigger">{t('disclaimer.title')}</span>
        <div className="disclaimer-content">
          {t('disclaimer.content')}
        </div>
      </div>
    </div>
  );
};

export default GameOverModal;
